import { useMemo, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PlusSmIcon, XIcon } from "@heroicons/react/outline";

import ReactTable from "components/lib/Global/ReactTable";
import { getFullName } from "utils";
import SubRowAsync from "./SubRowAsync";
import Checkbox from "components/lib/Shared/Checkbox";
import options from "components/PLCTABS/SFLdata/selectOptions";
import AddEmployee from "components/Employee/AddNewEmployee";
import { getEmployeeAsync } from "state/slices/employee";

const AssignPermission = () => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState([]);
  const [employee, setEmployee] = useState(false);
  const [recallEmployeeTable, setRecallEmployeeTable] = useState(false);

  const {
    companyOfficials,
    currentUserRow,
    getCompanyOfficialsStatus,
    dashboardRoles,
    company,
  } = useSelector((state) => state.companyDashboardSlice);

  const { getEmployee } = useSelector((state) => state.employeeSlice);
  const filteredOfficials = useMemo(() => {
    if (getCompanyOfficialsStatus.status !== "idle") return [];

    return companyOfficials.items.filter(
      (official) =>
        official?._id !== currentUserRow?._id &&
        filters.includes(official.designation)
    );
  }, [
    companyOfficials.items,
    currentUserRow,
    filters,
    getCompanyOfficialsStatus,
  ]);

  useEffect(() => {
    if (company?._id) {
      dispatch(getEmployeeAsync({ companyId: company._id }));
    }
    setRecallEmployeeTable(false);
  }, [dispatch, company, recallEmployeeTable]);

  const columns = useMemo(
    () => [
      {
        Header: "Full Name",
        accessor: "fullName",
        Cell: ({ row }) => getFullName(row.original.names) || "N/A",
      },
      {
        Header: "ID Type",
        accessor: "idType",
      },
      {
        Header: "Role",
        Cell: ({ row: { original } }) => original?.role?.join(", ") || "",
      },
      {
        id: "expander",
        Cell: ({ row, rows }) => {
          const onClick = () => {
            const alreadyExpandedRow = rows.find((r) => r.isExpanded);
            alreadyExpandedRow?.getToggleRowExpandedProps().onClick();
            if (alreadyExpandedRow?.id !== row.id) {
              row.getToggleRowExpandedProps().onClick();
            }
          };

          return (
            <span
              className="text-sm font-normal text-primary"
              {...row.getToggleRowExpandedProps()}
              onClick={onClick}
            >
              {row.isExpanded ? (
                <XIcon className="inline w-4 h-4 mr-1 text-tertiary" />
              ) : (
                <PlusSmIcon className="inline w-4 h-4 mr-1 text-tertiary" />
              )}
            </span>
          );
        },
      },
    ],
    []
  );

  const employeeColumns = useMemo(
    () => [
      {
        Header: "Full Name",
        accessor: "fullName",
        Cell: ({ row }) => getFullName(row?.original?.user?.names) || "N/A",
      },
      {
        Header: "ID Type",
        accessor: "idType",
        Cell: ({ row }) => row?.original?.user?.idType || "N/A",
      },
      {
        Header: "Role",
        Cell: "Employee",
      },
      {
        id: "expander",
        Cell: ({ row, rows }) => {
          const onClick = () => {
            const alreadyExpandedRow = rows.find((r) => r.isExpanded);
            alreadyExpandedRow?.getToggleRowExpandedProps().onClick();
            if (alreadyExpandedRow?.id !== row.id) {
              row.getToggleRowExpandedProps().onClick();
            }
          };

          return (
            <span
              className="text-sm font-normal text-primary"
              {...row.getToggleRowExpandedProps()}
              onClick={onClick}
            >
              {row.isExpanded ? (
                <XIcon className="inline w-4 h-4 mr-1 text-tertiary" />
              ) : (
                <PlusSmIcon className="inline w-4 h-4 mr-1 text-tertiary" />
              )}
            </span>
          );
        },
      },
    ],
    []
  );
  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => (
      <SubRowAsync
        row={row}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
      />
    ),
    []
  );
  const renderEmployeeRowSubComponent = useCallback(
    ({ row, visibleColumns }) => (
      <SubRowAsync
        row={row}
        visibleColumns={visibleColumns}
        employee={true}
        // setRecallEmployeeTable={setRecallEmployeeTable}
      />
    ),
    []
  );

  useEffect(() => {
    setFilters(options.companyOfficials.map((role) => role.value));
  }, []);

  const handleFilterChange = (e) => {
    const { checked, value } = e.target;
    setFilters((prevFilters) =>
      checked
        ? [...prevFilters, value]
        : prevFilters.filter((filter) => filter !== value)
    );
  };

  return (
    <>
      {companyOfficials.items.length > 0 ? (
        <>
          {!employee && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-500 text-sm">Officials</p>
                <button
                  className="bg-[#092c4c] text-white px-4 py-2 rounded shadow hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={() => setEmployee(true)}
                >
                  + Add Employee
                </button>
              </div>
              <hr />
            </div>
          )}
          {employee ? (
            <AddEmployee
              setEmployee={setEmployee}
              setRecallEmployeeTable={setRecallEmployeeTable}
              formType={"company"}
            />
          ) : (
            <>
              <div className="flex space-x-2 my-2">
                <p className="text-gray-500 text-sm mt-1">Filters</p>
                {options.companyOfficials.map((role) => (
                  <Checkbox
                    key={role.value}
                    name={role.label}
                    label={role.label}
                    checked={filters.includes(role.value)}
                    onChange={handleFilterChange}
                  />
                ))}
              </div>
              <hr />
              <div className="mt-6 w-full bg-white shadow">
                <ReactTable
                  columns={columns}
                  data={filteredOfficials}
                  renderRowSubComponent={renderRowSubComponent}
                  dataRowCellStyle="pl-4 leading-snug py-2"
                />
              </div>
              {getEmployee.items.length > 0 && (
                <div className="mt-6 w-full bg-white shadow">
                  <ReactTable
                    columns={employeeColumns}
                    data={getEmployee.items}
                    renderRowSubComponent={renderEmployeeRowSubComponent}
                    dataRowCellStyle="pl-4 leading-snug py-2"
                  />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="text-center">
          <svg
            className="w-12 h-12 mx-auto text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No Officials found
          </h3>
        </div>
      )}
    </>
  );
};

export default AssignPermission;
